import React, { useEffect, useRef, useState } from "react";
import { track } from "../../lib/analytics/track";
import { Play, Pause } from "lucide-react";

export default function AudioUnitPlayer({ programSlug, unitSlug, src, onComplete }) {
  const audioRef = useRef(null);
  const [marks, setMarks] = useState({ 25: false, 50: false, 75: false });
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [speedOpen, setSpeedOpen] = useState(false);
  const speedRef = useRef(null);

  useEffect(() => {
    setMarks({ 25: false, 50: false, 75: false });
  }, [src]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  // Formatear tiempo MM:SS
  const formatTime = (seconds) => {
    if (!seconds || !isFinite(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const onPlay = () => {
    setIsPlaying(true);
    track("audio_start", { programSlug, unitSlug });
  };

  const onPause = () => {
    setIsPlaying(false);
  };

  const onTimeUpdate = () => {
    const a = audioRef.current;
    if (!a || !a.duration || !Number.isFinite(a.duration)) return;

    if (!isDragging) {
      setCurrentTime(a.currentTime);
    }

    const pct = Math.floor((a.currentTime / a.duration) * 100);

    if (pct >= 25 && !marks[25]) {
      setMarks((m) => ({ ...m, 25: true }));
      track("audio_25", { programSlug, unitSlug });
    }
    if (pct >= 50 && !marks[50]) {
      setMarks((m) => ({ ...m, 50: true }));
      track("audio_50", { programSlug, unitSlug });
    }
    if (pct >= 75 && !marks[75]) {
      setMarks((m) => ({ ...m, 75: true }));
      track("audio_75", { programSlug, unitSlug });
    }
  };

  const onLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const onEnded = () => {
    setIsPlaying(false);
    track("audio_complete", { programSlug, unitSlug });
    onComplete?.();
  };

  const togglePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
    }
  };

  const handleProgressClick = (e) => {
    if (!audioRef.current || !duration) return;
    
    const bounds = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - bounds.left;
    const percentage = x / bounds.width;
    const newTime = percentage * duration;
    
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleProgressMouseDown = () => {
    setIsDragging(true);
  };

  const handleProgressMouseUp = () => {
    setIsDragging(false);
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    return false;
  };

  // Cerrar dropdown al click fuera
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (speedRef.current && !speedRef.current.contains(e.target)) {
        setSpeedOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const speedOptions = [1.0, 1.25, 1.5];

  return (
    <div className="bg-gradient-to-br from-slate-50 to-white rounded-2xl p-6 border border-slate-200">
      {/* Audio element oculto */}
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onPlay={onPlay}
        onPause={onPause}
        onTimeUpdate={onTimeUpdate}
        onLoadedMetadata={onLoadedMetadata}
        onEnded={onEnded}
        onContextMenu={handleContextMenu}
        className="hidden"
      />

      {/* Controles principales */}
      <div className="flex items-center gap-4 mb-4">
        {/* Botón Play/Pause */}
        <button
          onClick={togglePlayPause}
          className="w-12 h-12 rounded-full bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center transition-all shadow-lg hover:shadow-xl active:scale-95"
        >
          {isPlaying ? (
            <Pause className="w-5 h-5" fill="currentColor" />
          ) : (
            <Play className="w-5 h-5 ml-0.5" fill="currentColor" />
          )}
        </button>

        {/* Tiempo y velocidad */}
        <div className="flex items-center gap-3 text-sm text-slate-600 flex-1">
          <span className="font-medium tabular-nums">{formatTime(currentTime)}</span>
          <span className="text-slate-400">/</span>
          <span className="text-slate-500 tabular-nums">{formatTime(duration)}</span>
          
          {/* Control de velocidad — botón minimalista con dropdown */}
          <div className="ml-auto relative" ref={speedRef}>
            <button
              onClick={() => setSpeedOpen((v) => !v)}
              className="text-xs font-semibold tabular-nums px-2.5 py-1 rounded-md bg-blue-600 text-white shadow-sm hover:bg-blue-700 transition-colors"
            >
              {playbackRate === 1 ? '1x' : `${playbackRate}x`}
            </button>
            {speedOpen && (
              <div className="absolute bottom-full right-0 mb-2 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
                {speedOptions.map((speed) => (
                  <button
                    key={speed}
                    onClick={() => { setPlaybackRate(speed); setSpeedOpen(false); }}
                    className={`block w-full text-left text-xs font-semibold tabular-nums px-4 py-2 transition-colors ${
                      playbackRate === speed
                        ? 'bg-blue-50 text-blue-600'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    {speed}x
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Barra de progreso */}
      <div 
        className="relative h-2 bg-slate-200 rounded-full cursor-pointer group"
        onClick={handleProgressClick}
        onMouseDown={handleProgressMouseDown}
        onMouseUp={handleProgressMouseUp}
      >
        {/* Progreso */}
        <div 
          className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all"
          style={{ width: `${progress}%` }}
        />
        
        {/* Indicador de posición */}
        <div 
          className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white border-2 border-blue-600 rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ left: `calc(${progress}% - 8px)` }}
        />
      </div>
    </div>
  );
}
